import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, CheckCircle2, ChevronLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'

const GUIDE_CHECKOUT_URL = 'https://payhip.com/b/AvK9o?utm_source=bragbox_readiness_quiz&utm_medium=owned_page&utm_campaign=full_guide_primary'
const PREP_KIT_CHECKOUT_URL = 'https://payhip.com/b/Mm3WO?utm_source=bragbox_readiness_quiz&utm_medium=owned_page&utm_campaign=prep_kit_primary'
const TRACKER_CHECKOUT_URL = 'https://payhip.com/b/JAIdt?utm_source=bragbox_readiness_quiz&utm_medium=owned_page&utm_campaign=tracker_primary'
const COMPARE_URL = '/review-prep-kit-vs-brag-sheet-template.html?utm_source=bragbox_readiness_quiz&utm_medium=owned_page&utm_campaign=offer_compare'

const trackEvent = (eventName, params = {}) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', eventName, params)
  }
}

const questions = [
  {
    id: 'wins',
    question: 'How quickly could you list your 5 biggest wins from the last 6 months?',
    options: [
      { label: 'I would struggle to name 2', score: 0 },
      { label: 'I could name a few, but not confidently', score: 1 },
      { label: 'I could name most of them', score: 2 },
      { label: 'I already have them written down', score: 3 }
    ]
  },
  {
    id: 'metrics',
    question: 'Do you have numbers or proof behind your best work?',
    options: [
      { label: 'Almost none', score: 0 },
      { label: 'A little, but it is patchy', score: 1 },
      { label: 'Yes, for some wins', score: 2 },
      { label: 'Yes, for most of my strongest work', score: 3 }
    ]
  },
  {
    id: 'feedback',
    question: 'Can you pull manager, client, or peer feedback into your review?',
    options: [
      { label: 'No, I have not saved any', score: 0 },
      { label: 'I might find a few messages', score: 1 },
      { label: 'Yes, I have some useful examples', score: 2 },
      { label: 'Yes, I keep feedback in one place', score: 3 }
    ]
  },
  {
    id: 'business-impact',
    question: 'How clear are you on the business impact of your work?',
    options: [
      { label: 'Not clear at all', score: 0 },
      { label: 'I can describe effort, not impact', score: 1 },
      { label: 'I can explain some outcomes', score: 2 },
      { label: 'I can tie my work to results and priorities', score: 3 }
    ]
  },
  {
    id: 'self-review',
    question: 'If your self-review landed in your inbox tonight, what would happen?',
    options: [
      { label: 'Panic and a late night', score: 0 },
      { label: 'A scramble through Slack and email', score: 1 },
      { label: 'I could pull something decent together', score: 2 },
      { label: 'I would already have a solid draft path', score: 3 }
    ]
  },
  {
    id: 'promotion',
    question: 'Could you make a real case for a raise or promotion right now?',
    options: [
      { label: 'No, not with confidence', score: 0 },
      { label: 'Only with a lot of guesswork', score: 1 },
      { label: 'Yes, but it needs tightening up', score: 2 },
      { label: 'Yes, I have a clear evidence-based case', score: 3 }
    ]
  },
  {
    id: 'stories',
    question: 'How ready are your examples for review or interview conversations?',
    options: [
      { label: 'They are mostly in my head', score: 0 },
      { label: 'I have rough stories, not polished ones', score: 1 },
      { label: 'A few are ready to use', score: 2 },
      { label: 'I have sharp examples I can reuse anywhere', score: 3 }
    ]
  },
  {
    id: 'system',
    question: 'Do you have a simple system for keeping track going forward?',
    options: [
      { label: 'No system at all', score: 0 },
      { label: 'I rely on memory and good intentions', score: 1 },
      { label: 'I have something basic', score: 2 },
      { label: 'Yes, I can keep this up weekly', score: 3 }
    ]
  }
]

const getResult = (score) => {
  if (score <= 8) {
    return {
      tier: 'At Risk',
      headline: 'You are too close to a last-minute scramble.',
      body: 'You have likely done solid work, but the proof is scattered. That makes review season harder than it needs to be and weakens any raise or promotion conversation.',
      action: 'Performance Review Rescue gives you a quick way to pull your wins, evidence, and scripts into one usable system.'
    }
  }

  if (score <= 16) {
    return {
      tier: 'Needs Tightening',
      headline: 'You are not starting from zero, but your case is still loose.',
      body: 'You probably have enough material for a decent review, but it may still rely too much on memory, vague language, or half-saved evidence.',
      action: 'Performance Review Rescue helps you sharpen what you already have so your case sounds clear, credible, and ready to use.'
    }
  }

  return {
    tier: 'Strong Position',
    headline: 'You are in better shape than most people.',
    body: 'You already have useful raw material. The next step is packaging it properly so your review, promotion case, or raise ask lands cleanly.',
    action: 'Performance Review Rescue helps you turn that preparation into polished review language, stronger examples, and a faster final draft.'
  }
}

const getOffer = (score) => {
  if (score <= 8) {
    return {
      label: 'Get the £24 prep kit',
      href: PREP_KIT_CHECKOUT_URL,
      note: 'Best if your review is close and you need examples, structure, and wording tonight.'
    }
  }

  if (score <= 16) {
    return {
      label: 'Get the £27 guide',
      href: GUIDE_CHECKOUT_URL,
      note: 'Best if you have some raw material but need the fuller review, raise, and promotion walkthrough.'
    }
  }

  return {
    label: 'Get the £27 guide',
    href: GUIDE_CHECKOUT_URL,
    note: 'Best if you already have useful proof and need the fuller review, raise, and promotion walkthrough.'
  }
}

export default function PerformanceReviewReadiness() {
  const navigate = useNavigate()
  const [answers, setAnswers] = useState({})

  const totalScore = Object.values(answers).reduce((sum, value) => sum + value, 0)
  const answeredCount = Object.keys(answers).length
  const isComplete = answeredCount === questions.length
  const result = useMemo(() => getResult(totalScore), [totalScore])
  const offer = useMemo(() => getOffer(totalScore), [totalScore])
  const trackedResultKey = useRef(null)

  const selectAnswer = (id, score) => {
    setAnswers((prev) => ({ ...prev, [id]: score }))
  }

  const progress = Math.round((answeredCount / questions.length) * 100)

  useEffect(() => {
    if (!isComplete) return
    const resultKey = `${totalScore}:${result.tier}:${offer.label}`
    if (trackedResultKey.current === resultKey) return
    trackedResultKey.current = resultKey
    trackEvent('readiness_result_viewed', {
      event_category: 'engagement',
      score: totalScore,
      readiness_tier: result.tier,
      recommended_offer: offer.label
    })
  }, [isComplete, totalScore, result.tier, offer.label])

  const handleOfferClick = (placement) => {
    trackEvent('readiness_offer_click', {
      event_category: 'conversion_intent',
      score: totalScore,
      readiness_tier: result.tier,
      recommended_offer: offer.label,
      placement
    })
    window.location.href = offer.href
  }

  return (
    <div className="min-h-screen bg-[#F8F7F3]">
      <nav className="border-b border-gray-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <button onClick={() => navigate('/')} className="flex items-center">
            <img src="/assets/bragbox-hero.png" alt="BragBox" className="h-10" />
          </button>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => { window.location.href = COMPARE_URL }}>Compare paid options</Button>
            <Button variant="ghost" onClick={() => { window.location.href = PREP_KIT_CHECKOUT_URL }}>See the £24 prep kit</Button>
          </div>
        </div>
      </nav>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-8 items-start">
          <div>
            <p className="text-xs font-bold tracking-widest uppercase mb-4" style={{ color: '#C4A35A' }}>
              FREE SELF-CHECK
            </p>
            <h1 className="text-4xl md:text-5xl font-bold leading-tight mb-5" style={{ color: '#1F2A44' }}>
              Performance Review Readiness Score
            </h1>
            <p className="text-lg text-gray-700 max-w-2xl mb-6">
              Answer 8 quick questions and see how ready you really are for your next review, raise chat, or promotion conversation.
            </p>

            <div className="rounded-2xl border border-gray-200 bg-white p-5 mb-8">
              <div className="flex items-center justify-between text-sm text-gray-600 mb-3">
                <span>{answeredCount} of {questions.length} answered</span>
                <span>{progress}% complete</span>
              </div>
              <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${progress}%`, backgroundColor: '#1F2A44' }} />
              </div>
            </div>

            <div className="space-y-5">
              {questions.map((item, index) => (
                <div key={item.id} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                  <p className="text-sm font-semibold text-gray-500 mb-2">Question {index + 1}</p>
                  <h2 className="text-lg font-bold text-gray-900 mb-4">{item.question}</h2>
                  <div className="grid gap-3">
                    {item.options.map((option) => {
                      const selected = answers[item.id] === option.score
                      return (
                        <button
                          key={option.label}
                          onClick={() => selectAnswer(item.id, option.score)}
                          className={`text-left rounded-xl border px-4 py-3 transition ${selected ? 'border-[#1F2A44] bg-[#EEF2F7]' : 'border-gray-200 hover:border-gray-300 bg-white'}`}
                        >
                          <span className="text-sm md:text-base text-gray-800">{option.label}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:sticky lg:top-24 space-y-6">
            <div className="rounded-3xl p-6 md:p-8 shadow-sm" style={{ backgroundColor: '#1F2A44' }}>
              <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: '#C4A35A' }}>
                Your score
              </p>
              <div className="flex items-end gap-2 mb-3 text-white">
                <span className="text-5xl font-bold">{totalScore}</span>
                <span className="text-lg text-gray-300">/ 24</span>
              </div>
              <p className="inline-flex rounded-full bg-white/10 px-3 py-1 text-sm font-semibold text-white mb-4">
                {result.tier}
              </p>
              <h2 className="text-2xl font-bold text-white mb-3">{result.headline}</h2>
              <p className="text-gray-200 mb-4">{result.body}</p>
              <div className="rounded-2xl bg-white/10 p-4 mb-6">
                <p className="text-sm text-gray-100">{result.action}</p>
              </div>
              <Button size="lg" className="w-full text-base px-6 py-4 h-auto" style={{ backgroundColor: '#C4A35A', color: '#1F2A44' }} onClick={() => handleOfferClick('sidebar')}>
                {offer.label} <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <p className="text-xs text-gray-300 mt-3">{offer.note}</p>
              <p className="text-xs text-gray-300 mt-2">Instant access via Payhip, one-time purchase, no subscription.</p>
              <p className="text-xs text-gray-300 mt-2">
                Still not sure? <a href={COMPARE_URL} className="underline underline-offset-4 text-white">Compare the £19, £24, and £27 options first.</a>
              </p>
            </div>

            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-3">What the £27 guide gives you</h3>
              <div className="space-y-3">
                {[
                  'A faster way to gather wins, metrics, and feedback',
                  'Review and promotion templates you can actually use',
                  'Scripts for self-reviews, raise asks, and promotion chats'
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0" style={{ color: '#C4A35A' }} />
                    <p className="text-sm text-gray-700">{item}</p>
                  </div>
                ))}
              </div>
            </div>

            <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900">
              <ChevronLeft className="h-4 w-4" /> Back to BragBox home
            </button>
          </div>
        </div>

        {isComplete && (
          <div className="max-w-4xl mt-10 rounded-3xl border border-gray-200 bg-white p-6 md:p-8 shadow-sm">
            <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: '#C4A35A' }}>
              Your result in plain English
            </p>
            <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ color: '#1F2A44' }}>
              {result.headline}
            </h2>
            <p className="text-gray-700 mb-4">{result.body}</p>
            <p className="text-gray-700 mb-6">{result.action}</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="text-base px-8 py-4 h-auto" style={{ backgroundColor: '#1F2A44', color: 'white' }} onClick={() => handleOfferClick('results_panel')}>
                {offer.label} <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button size="lg" variant="outline" className="text-base px-8 py-4 h-auto border-2" style={{ borderColor: '#1F2A44', color: '#1F2A44' }} onClick={() => navigate('/performance-review-rescue')}>
                See what is inside the guide
              </Button>
            </div>
            <p className="mt-4 text-sm text-gray-600">{offer.note}</p>
            <p className="mt-3 text-sm text-gray-600">
              Still not sure? <a href={COMPARE_URL} className="font-semibold text-gray-900 underline">Compare the £19, £24, and £27 options first.</a>
            </p>
          </div>
        )}
      </section>
    </div>
  )
}
